import { Controller, type UseFormReturn } from 'react-hook-form';
import { SectionCard } from '@/components/common/SectionCard';
import { ChipGroup } from '@/components/ui/ChipGroup';
import { TextField } from '@/components/forms/TextField';
import { TextareaField } from '@/components/forms/TextareaField';
import { FieldLabel } from '@/components/forms/FieldLabel';
import type { CreateStartupFormValues } from '../../../schema';
import {
  FIELD_LABELS,
  FIELD_PLACEHOLDERS,
  MAX_DESCRIPTION_LENGTH,
  REVENUE_RANGE_OPTIONS,
  SECTION_TITLES,
} from '../../../constants';
import { StartupBusinessFields } from '../../StartupBusinessFields';

interface SectionProps {
  form: UseFormReturn<CreateStartupFormValues>;
}

/**
 * Step 1 "Financials & Fundraising" card (Figma 287:3098): revenue chips,
 * money inputs, and matching-critical fundraising fields.
 * Renders 8 revenue buckets per data-fields.md (Figma shows 6 - doc wins).
 */
export function Financials({ form }: SectionProps) {
  const {
    register,
    control,
    formState: { errors },
  } = form;

  return (
    <SectionCard title={SECTION_TITLES.FINANCIALS}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <FieldLabel label={FIELD_LABELS.totalRevenueRange} variant="caps" />
          <Controller
            control={control}
            name="totalRevenueRange"
            render={({ field }) => (
              <ChipGroup
                label={FIELD_LABELS.totalRevenueRange}
                options={REVENUE_RANGE_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                error={errors.totalRevenueRange?.message}
              />
            )}
          />
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <Controller
            control={control}
            name="arr"
            render={({ field }) => (
              <TextField
                {...field}
                numeric
                label={FIELD_LABELS.arr}
                inset
                prefix="$"
                placeholder={FIELD_PLACEHOLDERS.money}
                error={errors.arr?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="amountRaisedToDate"
            render={({ field }) => (
              <TextField
                {...field}
                numeric
                label={FIELD_LABELS.amountRaisedToDate}
                inset
                prefix="$"
                placeholder={FIELD_PLACEHOLDERS.money}
                error={errors.amountRaisedToDate?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="nondilutiveFunding"
            render={({ field }) => (
              <TextField
                {...field}
                numeric
                label={FIELD_LABELS.nondilutiveFunding}
                inset
                prefix="$"
                placeholder={FIELD_PLACEHOLDERS.money}
                error={errors.nondilutiveFunding?.message}
              />
            )}
          />
        </div>

        <TextareaField
          {...register('recentMilestone')}
          label={FIELD_LABELS.recentMilestone}
          rows={3}
          maxLength={MAX_DESCRIPTION_LENGTH}
          showCount
          placeholder={FIELD_PLACEHOLDERS.recentMilestone}
          error={errors.recentMilestone?.message}
        />

        <StartupBusinessFields register={register} errors={errors} />
      </div>
    </SectionCard>
  );
}
